import { extranetApiClient } from './extranetApiClient';
import type {
  ExtranetEnvironmentsPayload,
  ExtranetLoginRequest,
  ExtranetLoginResponse,
  ExtranetMeResponse,
} from '../auth/types';

/** `data.config` of `GET /extranet/config` (contract §4). */
export interface ExtranetConfigResponse {
  data?: { config?: Record<string, unknown> };
}

export const extranetAuthService = {
  /**
   * VEOS environments the login form may offer (contract §1, public).
   *
   * @param tenantSlug - Tenant slug from the URL.
   * @param debug      - `true` when the page URL carries `?debug=1` — forces
   *                     the selector on a production deployment.
   */
  async getEnvironments(
    tenantSlug: string,
    debug = false,
  ): Promise<ExtranetEnvironmentsPayload> {
    const response = await extranetApiClient.get<{ data: ExtranetEnvironmentsPayload }>(
      '/extranet/auth/environments',
      {
        params: debug ? { tenant_slug: tenantSlug, debug: 1 } : { tenant_slug: tenantSlug },
        // An unknown tenant answers 404 — the login page degrades to the
        // plain form, so no global error toast either.
        suppressErrorToast: true,
      },
    );
    return response.data.data;
  },

  async login(credentials: ExtranetLoginRequest): Promise<ExtranetLoginResponse> {
    const response = await extranetApiClient.post<ExtranetLoginResponse>(
      '/extranet/auth/login',
      { language: 'fr', ...credentials },
    );
    return response.data;
  },

  async logout(): Promise<void> {
    await extranetApiClient.post('/extranet/auth/logout');
  },

  async me(): Promise<ExtranetMeResponse> {
    const response = await extranetApiClient.get<ExtranetMeResponse>('/extranet/auth/me');
    return response.data;
  },

  /**
   * Module config (brand theme, …). When a tenant identifier (slug or UUID)
   * is supplied it is sent as an explicit `X-Tenant-ID` header, which wins
   * over the stored tenant so the anonymous, pre-login fetch resolves the
   * tenant from the URL slug (same as claimflow).
   */
  async getConfig(tenantId?: string): Promise<ExtranetConfigResponse> {
    const config = tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : undefined;
    const response = await extranetApiClient.get<ExtranetConfigResponse>(
      '/extranet/config',
      config,
    );
    return response.data;
  },
};
